import { AxiosProgressEvent, CancelTokenSource } from 'axios';

import BaseAPI from 'utils/api/base';

import {
  ActivityListResponse as VatixActivityListResponse,
  MessageActivityResponse,
  ActivityResponse as VatixActivityResponse,
} from 'ui/types';

import {
  QueryParams,
  PaginationParams,
  APIRequest,
  OrderingParams,
  UuidableName,
  SearchResponse,
} from 'utils/api/types';

import { DeviceListQueryParams } from 'stores/DeviceList/types';
import { LOCATION_HISTORY_PERIOD } from 'core/constants';

import {
  LocationHistoryResponse,
  DeviceListPaginatedResponse,
  DeviceDetailsResponse,
  DeviceAnalyticsType,
  DeviceUpdateData,
  DevicesSearchResponse,
  DeviceHistoryPaginatedResponse,
} from './devices.types';

class DevicesAPI extends BaseAPI {
  loadDeviceActivities = (deviceId: string, params: PaginationParams): APIRequest<VatixActivityListResponse> => {
    const method = 'GET';
    const url = `/devices/${deviceId}/activities/`;

    return this.request<VatixActivityListResponse>({
      url,
      method,
      params,
    });
  };

  addDeviceMessage = (deviceId: string, message: string): APIRequest<MessageActivityResponse> => {
    const url = `/devices/${deviceId}/comments/`;
    const method = 'POST';

    return this.request<MessageActivityResponse>({
      url,
      method,
      data: { message },
    });
  };

  updateDeviceMessage = (deviceId: string, messageId: string, message: string): APIRequest<MessageActivityResponse> => {
    const url = `/devices/${deviceId}/comments/${messageId}/`;
    const method = 'PUT';

    return this.request<MessageActivityResponse>({
      url,
      method,
      data: { message },
    });
  };

  removeDeviceMessage = (deviceId: string, messageId: string): APIRequest<MessageActivityResponse> => {
    const url = `/devices/${deviceId}/comments/${messageId}/`;
    const method = 'DELETE';

    return this.request<MessageActivityResponse>({
      url,
      method,
    });
  };

  uploadDeviceFile = (
    deviceId: string,
    file: File,
    onUploadProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): APIRequest<VatixActivityResponse> => {
    const method = 'POST';
    const url = `/devices/${deviceId}/activities/files/`;

    const data = new FormData();
    data.append('file', file);

    return this.request<VatixActivityResponse>({
      url,
      method,
      data,
      onUploadProgress,
      cancelTokenSource,
    });
  };

  removeDeviceFile = (deviceId: string, fileId: string): APIRequest<void> => {
    const url = `/devices/${deviceId}/activities/files/${fileId}/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
    });
  };

  loadDeviceList = (params: DeviceListQueryParams): APIRequest<DeviceListPaginatedResponse> => {
    const url = '/devices/';
    const method = 'GET';

    return this.request<DeviceListPaginatedResponse>({
      url,
      method,
      params,
    });
  };

  loadDeviceAnalytics = (params: QueryParams): APIRequest<DeviceAnalyticsType[]> => {
    const url = '/devices/analytics/';
    const method = 'GET';

    return this.request<DeviceAnalyticsType[]>({
      url,
      method,
      params,
    });
  };

  updateDevice = (deviceId: string, data: DeviceUpdateData): APIRequest<DeviceDetailsResponse> => {
    const url = `/devices/${deviceId}/`;
    const method = 'PATCH';

    return this.request<DeviceDetailsResponse>({
      url,
      method,
      data,
    });
  };

  loadDeviceDetails = (deviceId: string): APIRequest<DeviceDetailsResponse> => {
    const url = `/devices/${deviceId}/`;
    const method = 'GET';

    return this.request<DeviceDetailsResponse>({
      url,
      method,
    });
  };

  searchDevices = (
    params?: Partial<{
      query: string;
      excluded_group?: string;
      limit: number;
      assigned?: boolean;
    }>
  ): APIRequest<DevicesSearchResponse[]> => {
    const url = '/devices/search/';
    const method = 'GET';

    return this.request({
      url,
      method,
      params,
    });
  };

  searchDeviceTags = (
    params?: Partial<{
      query: string;
      limit: number;
    }>
  ): APIRequest<SearchResponse[]> => {
    const url = '/devices/tags/search/';
    const method = 'GET';

    return this.request<SearchResponse[]>({
      url,
      method,
      params,
    });
  };

  deviceTagsDetails = (uuid: string): APIRequest<UuidableName> => {
    const url = `/devices/tags/${uuid}/`;
    const method = 'GET';

    return this.request<UuidableName>({
      url,
      method,
    });
  };

  assignedDevicesUsers = (
    params?: Partial<{
      query: string;
      limit: number;
    }>
  ): APIRequest<SearchResponse[]> => {
    const url = '/devices/users/search/';
    const method = 'GET';

    return this.request<SearchResponse[]>({
      url,
      method,
      params,
    });
  };

  loadDeviceLocations = (
    deviceId: string,
    params: { period?: keyof typeof LOCATION_HISTORY_PERIOD },
    timeout?: number
  ): APIRequest<LocationHistoryResponse[]> => {
    const url = `/devices/${deviceId}/locations/`;
    const method = 'GET';

    return this.request<LocationHistoryResponse[]>({
      url,
      method,
      params,
      timeout,
    });
  };

  loadDeviceHistory = (
    deviceId: string,
    params?: OrderingParams | QueryParams
  ): APIRequest<DeviceHistoryPaginatedResponse> => {
    const url = `/devices/${deviceId}/assignments/`;
    const method = 'GET';

    return this.request<DeviceHistoryPaginatedResponse>({
      url,
      method,
      params,
    });
  };
}

export default DevicesAPI;
